import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import RenderWithTooltip from "../../../../../../../../components/RenderWithTooltip";
import ActionMenu from "../../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../../components/app/DeleteModal";
import { useRoleController } from "../../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { useArchivePatientDiagnoses } from "../../../../../../../../queries/diagnoses/use-patient-diagnoses-archive";
import { usePatientDiagnosesList } from "../../../../../../../../queries/diagnoses/use-patient-diagnoses-list";
import { PatientDiagnosis } from "../../../../../../../../services";
import { formatDate } from "../../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  getXtenantId,
  roleFilter,
} from "../../../../../../../../utils/utils";
import UpsertDiagnosesDrawer from "../../diagnoses/UpsertDiagnosisDrawer";
import { TYPE_OPTION_MAPPER } from "../../diagnoses/diagnosis-constant";
// import { Status } from "../../../../../../../../constants/provider/enums";
import StatusChip from "../../../../../../../../components/StatusChip";

const ACTION_MENU_OPTIONS = [
  { value: "edit", label: "Edit" },
  { value: "delete", label: "Delete" },
];

interface DiagnosisListProps {
  isInsideVisitNote?: boolean;
}

const DiagnosisList = ({ isInsideVisitNote = false }: DiagnosisListProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams() as { patientId: any };
  const diagnosesDisclosure = useDisclosure(false);
  const { data: diagnosisList, refetch } = usePatientDiagnosesList({
    patientUuid: patientId,
    pagination,
    gridSorting,
    archive: false,
    xTENANTID: getXtenantId() ?? undefined,
  });

  const { mutateAsync: archiveDiagnosis } = useArchivePatientDiagnoses();
  const deleteDiagnosisDisclosure = useDeleteDisClosure();
  const { hasReadOnly, hasUpdate, hasDelete } = useRoleController("Clinical");

  const COLUMNS = applyDefaultColumnSettings<PatientDiagnosis>(
    [
      {
        field: "medicalCode",
        headerName: "ICD",
        flex: 0.4,
        minWidth: 120,
        renderCell: ({ value }) => <RenderWithTooltip value={value?.code} />,
      },
      {
        field: "name",
        headerName: "Diagnosis Name",
        flex: 0.5,
        minWidth: 140,
        renderCell: ({ row }) => (
          <RenderWithTooltip value={row?.medicalCode?.description} />
        ),
      },
      {
        field: "type",
        headerName: "Type",
        flex: 0.2,
        minWidth: 70,
        renderCell: ({ value }) => TYPE_OPTION_MAPPER[value] ?? "-",
      },

      {
        field: "diagnosed_date",
        headerName: "Diagnosed Date",
        flex: 0.3,
        minWidth: 110,
        renderCell: ({ row }) =>
          row?.diagnosedDate ? formatDate(row?.diagnosedDate) : "-",
      },
      {
        field: "modified",
        headerName: "Updated Date",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ row }) =>
          row?.modifiedDate ? formatDate(row?.modifiedDate) : "-",
      },
      {
        field: "active",
        headerName: "Status",
        flex: 0.2,
        minWidth: 80,
        renderCell: ({ value }) => <StatusChip value={value} />,
      },
      // {
      //   field: "note",
      //   headerName: "Note",
      //   flex: 0.5,
      //   minWidth: 120,
      //   renderCell: ({ value }) => (
      //     <RenderWithTooltip value={value} />
      //   ),
      // },
      {
        field: "Action",
        headerName: "Actions",
        width: 70,
        hideSortIcons: true,
        headerAlign: "center",
        renderCell: ({ row }: any) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: ACTION_MENU_OPTIONS,
                compWith: {
                  edit: hasUpdate,
                  delete: hasDelete,
                },
              })}
              onActionClick={(v) => {
                if (v === "edit") {
                  const transformedRow = {
                    ...row,
                    medicalCode: row?.medicalCode
                      ? {
                          uuid: row?.medicalCode.uuid,
                          code: `${row?.medicalCode.code} - ${row?.medicalCode?.description}`,
                        }
                      : undefined,
                  };

                  diagnosesDisclosure.open(transformedRow);
                } else deleteDiagnosisDisclosure.open(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["diagnosed_date", "modified"]
  );

  function archiveHandler<T>(value: boolean, id: T) {
    return archiveDiagnosis({
      patientDiagnosisId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <>
      <UpsertDiagnosesDrawer disclosureHost={diagnosesDisclosure} />

      <DeleteModal
        w="380px"
        open={deleteDiagnosisDisclosure.opened}
        onClose={deleteDiagnosisDisclosure.close}
        id={deleteDiagnosisDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Diagnosis"}
      />

      {/* <ChartTableWrapper> */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={diagnosisList?.data?.content ?? []}
        rowCount={diagnosisList?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, hasReadOnly || isInsideVisitNote)}
        // sx={{ marginTop: "40px" }}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        sortModel={gridSorting}
        onSortModelChange={setGridSorting}
      />
      {/* </ChartTableWrapper> */}
    </>
  );
};

export default DiagnosisList;
